import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { ActionIcon, Button, Menu, Tooltip } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DS_AI_MENU_PROPS } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/AnalysisWorkspace/components/primitives/dropdownMenu.styles';
import {
	DS_AI_EXIT_AT_ONCE_MS,
	DS_AI_TOOLTIP_PROPS,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { useOpenIngestion } from '../useOpenIngestion';

interface Props {
	/** Existing "Add transaction" label, reused for the "+" button's tooltip. */
	addTransactionLabel: string;
	onAddTransaction: () => void;
	onImportCsv: () => void;
	isImportCsvLoading: boolean;
	disabled: boolean;
}

/** "+" and its "Add transactions" menu: Import from CSV or Extract from document. */
export const AddTransactionsMenu = ({
	addTransactionLabel,
	onAddTransaction,
	onImportCsv,
	isImportCsvLoading,
	disabled,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const openIngestion = useOpenIngestion();

	return (
		<>
			<Tooltip {...DS_AI_TOOLTIP_PROPS} label={addTransactionLabel} withArrow>
				<ActionIcon
					variant="default"
					c="dark.8"
					size={36}
					aria-label={addTransactionLabel}
					onClick={onAddTransaction}
					disabled={disabled}
					data-testid={TEST_ELEMENT_IDS.CAP_ADD_TRANSACTION_BUTTON}
				>
					<Icon variant="add" size="lg" />
				</ActionIcon>
			</Tooltip>
			{/* Closes at once: a fading menu would show through the extract modal as it fades in. */}
			<Menu
				{...DS_AI_MENU_PROPS}
				transitionProps={{
					...DS_AI_MENU_PROPS.transitionProps,
					exitDuration: DS_AI_EXIT_AT_ONCE_MS,
				}}
				width={240}
			>
				<Menu.Target>
					<Button
						size="height-36"
						rightSection={<Icon variant="caret-down" size="lg" />}
						loading={isImportCsvLoading}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_ADD_TRANSACTIONS_MENU}
					>
						{tCapManage('dealstack_ai.ingest.entry.add_transactions')}
					</Button>
				</Menu.Target>
				<Menu.Dropdown>
					<Menu.Item
						leftSection={<Icon variant="upload-v3-thin" size="lg" />}
						onClick={onImportCsv}
						disabled={disabled}
						data-testid={TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_IMPORT_CSV_ITEM}
					>
						{tCapManage('dealstack_ai.ingest.entry.import_from_csv')}
					</Menu.Item>
					<Menu.Item
						leftSection={<IconSparkles size={16} stroke={1.5} />}
						onClick={openIngestion}
						data-testid={
							TEST_ELEMENT_IDS.DEALSTACK_AI_INGEST_EXTRACT_FROM_DOCUMENT_ITEM
						}
					>
						{tCapManage('dealstack_ai.ingest.entry.extract_from_document')}
					</Menu.Item>
				</Menu.Dropdown>
			</Menu>
		</>
	);
};
